import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Clock3, Search, ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { shopifyFetch, COLLECTIONS_QUERY, nodes } from "@/lib/shopify";

export default function Navbar() {
  const { count, openCart } = useCart();
  const [collections, setCollections] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    shopifyFetch(COLLECTIONS_QUERY, { first: 8 })
      .then((d) =>
        setCollections(nodes(d.collections).filter((c) => c.handle !== "frontpage"))
      )
      .catch(() => {});
  }, []);

  const links = [
    { label: "Shop All", to: "/shop" },
    ...collections.slice(0, 3).map((c) => ({ label: c.title, to: `/collections/${c.handle}` })),
    { label: "Contact", to: "/contact" },
  ];

  return (
    <>
      <header
        data-testid="site-navbar"
        className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="group flex items-center gap-2.5"
            onClick={() => setMobileOpen(false)}
          >
            <Clock3
              className="h-6 w-6 text-amber transition-transform duration-700 group-hover:rotate-[360deg]"
              strokeWidth={2.2}
            />
            <span className="font-display text-lg font-black tracking-tight text-ivory">
              WEIRD O'CLOCK
            </span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop-links">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-ivory/70 transition-colors hover:text-amber"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-4">
            <button
              data-testid="nav-search-button"
              onClick={() => navigate("/search")}
              className="rounded-full p-2 text-ivory/80 transition-colors hover:bg-white/10 hover:text-amber"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              data-testid="nav-cart-trigger-button"
              onClick={openCart}
              className="relative rounded-full p-2 text-ivory/80 transition-colors hover:bg-white/10 hover:text-amber"
              aria-label="Open cart"
            >
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span
                  data-testid="nav-cart-count-badge"
                  className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber font-mono2 text-[10px] font-bold text-void"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setMobileOpen(true)}
              className="rounded-full p-2 text-ivory/80 hover:bg-white/10 lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-nav-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col bg-void/95 backdrop-blur-2xl"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-display text-lg font-black text-ivory">WEIRD O'CLOCK</span>
              <button
                data-testid="mobile-nav-close-button"
                onClick={() => setMobileOpen(false)}
                className="rounded-full p-2 text-ivory hover:bg-white/10"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 font-display text-4xl font-black tracking-tight text-ivory transition-colors hover:text-amber"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-8 border-t border-white/10 pt-6"
              >
                <Link
                  to="/shipping-returns"
                  data-testid="mobile-nav-link-shipping"
                  onClick={() => setMobileOpen(false)}
                  className="font-mono2 text-xs uppercase tracking-[0.25em] text-ivory/50 hover:text-amber"
                >
                  Shipping & Returns
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
